import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useOrders } from "../../hooks/useOrders";

export default function OrdersPage() {
    const { orders, loading, pagination, fetchOrders, deleteOrderById } = useOrders();
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(1);

    useEffect(() => {
        fetchOrders({ page, limit: 10, status: statusFilter });
    }, [fetchOrders, page, statusFilter]);

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered': return "bg-success text-white";
            case 'shipped': return "bg-info text-dark";
            case 'processing': return "bg-warning text-dark";
            case 'cancelled': return "bg-danger text-white";
            case 'placed': return "bg-primary text-white";
            default: return "bg-secondary text-white"; // Pending
        }
    };

    const statusOptions = [
        { value: "", label: "All Statuses" },
        { value: "Placed", label: "Placed" },
        { value: "Processing", label: "Processing" },
        { value: "Shipped", label: "Shipped" },
        { value: "Delivered", label: "Delivered" },
        { value: "Cancelled", label: "Cancelled" }
    ];

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-1 fw-bolder text-dark">Orders Management</h2>
                    <p className="text-muted mb-0 small">View, manage, and track customer orders.</p>
                </div>
                <span className="badge bg-primary fs-6 px-3 py-2 rounded-pill shadow-sm">
                    {pagination.totalCount} Total Orders
                </span>
            </div>

            <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                <div className="card-header bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <h5 className="mb-0 fw-bold text-secondary">Recent Orders</h5>

                    <div className="d-flex align-items-center">
                        <label className="text-muted small me-2 fw-semibold">Filter:</label>

                        <Select
                            options={statusOptions}
                            value={statusOptions.find(option => option.value === statusFilter)}
                            onChange={(selectedOption) => {
                                setStatusFilter(selectedOption ? selectedOption.value : "");
                                setPage(1);
                            }}

                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}

                            styles={{
                                control: (baseStyles) => ({
                                    ...baseStyles,
                                    minWidth: '160px',
                                    borderColor: '#dee2e6',
                                    boxShadow: 'none',
                                    '&:hover': { borderColor: '#adb5bd' }
                                }),
                                option: (baseStyles, state) => ({
                                    ...baseStyles,
                                    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e9ecef' : 'white',
                                    color: state.isSelected ? 'white' : 'black',
                                }),
                                menuPortal: base => ({ ...base, zIndex: 9999 })
                            }}
                            isSearchable={false}
                        />
                    </div>
                </div>

                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 text-nowrap">
                            <thead className="table-light">
                                <tr>
                                    <th className="px-4 py-3 text-muted border-0" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>ORDER ID</th>
                                    <th className="py-3 text-muted border-0" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>DATE</th>
                                    <th className="py-3 text-muted border-0" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>CUSTOMER</th>
                                    <th className="py-3 text-muted border-0" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>TOTAL</th>
                                    <th className="py-3 text-muted border-0" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>STATUS</th>
                                    <th className="px-4 py-3 text-muted border-0 text-end" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && orders.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary border-3" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : orders.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="text-muted mb-2">📦</div>
                                            <h6 className="text-muted fw-semibold">No orders found.</h6>
                                            <p className="text-muted small mb-0">Try adjusting your filters.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    orders.map(order => (
                                        <tr key={order._id} className="transition-all bg-white">
                                            <td className="px-4 py-4 text-primary fw-medium">
                                                #{order._id.substring(order._id.length - 6).toUpperCase()}
                                            </td>
                                            <td className="py-4 text-dark fw-medium small">
                                                {new Date(order.createdAt).toLocaleDateString('en-US', {
                                                    year: 'numeric', month: 'short', day: 'numeric'
                                                })}
                                            </td>
                                            <td className="py-4">
                                                <div className="text-dark fw-medium">{order.userId?.name || order.customerName || "Guest"}</div>
                                                <div className="small text-muted" style={{ fontSize: '0.8rem' }}>{order.userId?.email || order.customerEmail || ""}</div>
                                            </td>
                                            <td className="py-4 text-dark fw-medium">
                                                ₹{order.totalPrice?.toFixed(2) || "0.00"}
                                            </td>
                                            <td className="py-4">
                                                <span className={`badge ${getStatusBadge(order.orderStatus)} px-3 py-2 rounded-pill fw-medium`}>
                                                    {order.orderStatus || 'Pending'}
                                                </span>
                                            </td>
                                            <td className="py-4 text-end">
                                                <div className="d-flex justify-content-centar align-items-centergap-2">
                                                    <Link
                                                        to={`/ecommerce/orders/${order._id}`}
                                                        className="btn btn-sm btn-outline-primary mr-2 bg-white fw-medium px-3"
                                                    >
                                                        Details
                                                    </Link>
                                                    <button
                                                        onClick={() => deleteOrderById(order._id)}
                                                        className="btn btn-sm btn-outline-danger bg-white fw-medium px-3"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {pagination.totalPages > 1 && (
                    <div className="card-footer bg-light border-top py-3 d-flex justify-content-between align-items-center px-4">
                        <span className="small text-muted fw-semibold">
                            Showing Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <div className="btn-group shadow-sm">
                            <button
                                className="btn btn-sm btn-white border bg-white"
                                disabled={page === 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                ← Prev
                            </button>
                            <button
                                className="btn btn-sm btn-white border bg-white"
                                disabled={page === pagination.totalPages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}